/**
 * Rendering helpers for stored rich text.
 *
 * The course editor is a contentEditable, and contentEditable leaves the first
 * paragraph as a bare text node — only the paragraphs after it get wrapped in a
 * `<div>`. A text node has no box, so no stylesheet can put space beneath it,
 * and a multi-paragraph lesson rendered as one unbroken wall of text no matter
 * what the `.prose` rules said. Wrapping those loose runs in a real `<p>` gives
 * every paragraph something to be spaced.
 */

/**
 * Wrap top-level text runs in `<p>` so every paragraph is a block.
 *
 * Input is HTML the server already sanitized on write, parsed here with
 * DOMParser (which never executes scripts) and re-serialized — so this only
 * ever rewraps existing content and cannot introduce markup the sanitizer
 * would have stripped.
 */
export function normalizeRichText(html) {
  const source = String(html || '');
  if (!source.trim()) return '';
  if (typeof DOMParser === 'undefined') return source;

  let body;
  try {
    body = new DOMParser().parseFromString(`<body>${source}</body>`, 'text/html').body;
  } catch {
    return source;
  }
  if (!body) return source;

  // Gather first: wrapping as we walk would mutate the list we are walking.
  const runs = [];
  let current = null;
  for (const node of Array.from(body.childNodes)) {
    const isLooseText = node.nodeType === Node.TEXT_NODE && node.textContent.trim();
    // An inline element sitting at the top level — <b>, <a>, <span> — belongs to
    // the same paragraph as the text around it.
    const isInline = node.nodeType === Node.ELEMENT_NODE
      && ['B', 'STRONG', 'I', 'EM', 'U', 'A', 'SPAN', 'BR'].includes(node.tagName);

    if (isLooseText || isInline) {
      if (!current) { current = []; runs.push(current); }
      current.push(node);
    } else if (node.nodeType !== Node.TEXT_NODE) {
      current = null;
    }
  }

  if (!runs.length) return source;

  for (const run of runs) {
    // A run of nothing but <br> is spacing, not a paragraph — leave it alone.
    if (run.every((node) => node.nodeName === 'BR')) continue;
    const paragraph = body.ownerDocument.createElement('p');
    run[0].parentNode.insertBefore(paragraph, run[0]);
    run.forEach((node) => paragraph.appendChild(node));
  }

  return body.innerHTML;
}
